import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ShoppingCart,
  Plus,
} from "lucide-react";
import api from "../lib/api";
import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../theme";
import { useAuth } from "../contexts/AuthContext";

// Skeleton Loader for products
const SkeletonCard = () => (
  <div
    style={{
      flex: "0 0 148px",
      minWidth: "148px",
      maxWidth: "148px",
      height: 220,
      borderRadius: 14,
      background: "linear-gradient(90deg, #e0dce8 25%, #ede7f4 50%, #e0dce8 75%)",
      backgroundSize: "200% 100%",
      animation: "skeleton-loading 1.2s infinite linear",
      border: "1px solid #f0ecf7",
    }}
  />
);

const skeletonStyle = `
  @keyframes skeleton-loading {
    0% { background-position: 200% 0 }
    100% { background-position: -200% 0 }
  }
`;

export default function Categories() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [category_, setCategory] = useState([]);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();
  const { profile } = useAuth();

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const resp = await api.get(`api/medicines/category/${id}`);
        setCategory(resp.data.medicines || []);
      } catch (err) {
        console.error("Error fetching category", err);
        setCategory([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCategory();
  }, [id]);

  return (
    <div style={{ padding: "20px 24px", maxWidth: "1200px", margin: "0 auto", minHeight: "100vh", background: "#f3f0f8" }}>
      <style>{skeletonStyle}</style>

      {/* Header */}
      <div
        style={{
          background: "transparent",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          padding: "0 0 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
          boxShadow: "none",
        }}
      >
        <i
          className="fi fi-br-angle-left"
          onClick={() => navigate(-1)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            cursor: "pointer",
            fontSize: "20px",
            padding: "6px 10px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            color: "#2d2536",
          }}
        ></i>
        <motion.h2
          style={{
            margin: 0,
            fontWeight: 700,
            fontSize: 18,
            color: "#18141f",
            textAlign: "center",
            flex: 1,
          }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {id}
        </motion.h2>
        {/* Cart */}
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            background: "rgba(255,255,255,0.8)",
            boxShadow: "0 4px 14px rgba(77, 30, 126, 0.12)",
            backdropFilter: "blur(8px)",
          }}
          onClick={() => navigate("/cart")}
        >
          <ShoppingCart size={18} color="#2d2536" />
          <span
            style={{
              position: "absolute",
              top: 6,
              right: 8,
              width: 8,
              height: 8,
              background: "#ff4b6e",
              borderRadius: "50%",
            }}
          />
        </div>
      </div>

      {/* Category Title */}
      <h5
        style={{
          marginBottom: 14,
          padding: "0",
          borderRadius: 0,
          textAlign: "left",
          background: "transparent",
          color: "#18141f",
          fontWeight: 700,
          fontSize: 16,
        }}
      >
        Browse {id}
      </h5>

      {/* Products Grid */}
      {loading ? (
        <div style={{ display: "flex", overflowX: "auto", gap: 12, paddingBottom: 10, scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}>
          {[...Array(4)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : category_.length === 0 ? (
        <div style={{ textAlign: "center", color: "#999", padding: 40 }}>
          No products found in <strong>{id}</strong>
        </div>
      ) : (
        <div style={{ display: "flex", overflowX: "auto", gap: 12, paddingBottom: 10, scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}>
          {category_.map((item, index) => {
            const imageUrl = item.images && item.images.length > 0 ? item.images[0] : "";
            return (
              <div
                key={index}
                onClick={() => navigate(`/product/${item._id}`)}
                style={{
                  flex: "0 0 148px",
                  minWidth: "148px",
                  maxWidth: "148px",
                  scrollSnapAlign: "start",
                  borderRadius: 14,
                  overflow: "hidden",
                  background: "#f7f7f8",
                  border: "1px solid #efedf2",
                  boxShadow: "0 6px 14px rgba(30, 24, 43, 0.06)",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = "0 10px 20px rgba(30, 24, 43, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 6px 14px rgba(30, 24, 43, 0.06)";
                }}
              >
                <img
                  src={imageUrl}
                  alt={item.name}
                  style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: "14px 14px 0 0", background: "#ededf0" }}
                />
                <button
                  type="button"
                  aria-label={`Add ${item.name} to cart`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/product/${item._id}`);
                  }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: 90,
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    border: "none",
                    background: "#0f0f10",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 20px rgba(0,0,0,0.22)",
                    cursor: "pointer",
                  }}
                >
                  <Plus size={16} />
                </button>
                <div style={{ padding: "10px 10px 12px", textAlign: "left", color: "#18141f" }}>
                  <h5 style={{ margin: "0", display: "block", width: "100%", fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</h5>
                  <p style={{ fontSize: 11, color: "#8f899a", margin: "2px 0 4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {item.category}
                  </p>
                  <strong style={{ fontSize: 15, color: "#1f1a28" }}>${item.price}</strong>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
